import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class NavigationLoggerService {
  private readonly router: Router = inject(Router);

  // Single source of truth for all known routes.
  // parent: fallback URL when history is empty (defaults to the prefix itself)
  // logAllSubPaths: log any sub-path, not just /{prefix}/{id} (e.g. no list view, or multi-segment detail)
  // logBareRoute: log this URL as a loggable page even when it has no /{id} suffix
  private readonly routes: { prefix: string; parent?: string; logAllSubPaths?: boolean; logBareRoute?: boolean }[] = [
    { prefix: '/sites' },
    { prefix: '/assets' },
    { prefix: '/devices' },
    { prefix: '/device' },
    { prefix: '/devices-configuration' },
    { prefix: '/device-templates' },
    { prefix: '/peripherals' },
    { prefix: '/asset-events' },
    { prefix: '/device-events' },
    { prefix: '/reports' },
    { prefix: '/connectors' },
    { prefix: '/schedulers', logAllSubPaths: true },
    { prefix: '/platform-event-rules' },
    { prefix: '/on-call-management/topics' },
    { prefix: '/on-call-management/calendars' },
    { prefix: '/on-call-management/team-plannings' },
    { prefix: '/on-call-management/escalation-protocols' },
    { prefix: '/stocks', parent: '/sites', logAllSubPaths: true },
    { prefix: '/asset-groups', parent: '/assets', logAllSubPaths: true },
    { prefix: '/funnel', parent: '/sites', logBareRoute: true }
  ];

  private navigationHistory: string[] = [];
  private previousUrl: string | null = null;
  private isBackNavigation = false;
  private urlToSkip: string | null = null;

  constructor() {
    this.initializeNavigationTracking();
  }

  // Subscribes to router events and delegates each completed navigation to handleNavigation.
  private initializeNavigationTracking(): void {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.handleNavigation(event.url);
    });
  }

  // Decides whether to push, pop, or reset history based on the navigation type.
  private handleNavigation(currentUrl: string): void {
    if (!this.previousUrl) {
      this.previousUrl = currentUrl;
      return;
    }

    if (this.isBackNavigation) {
      this.isBackNavigation = false;
      this.navigationHistory = this.navigationHistory.slice(0, -1);
      this.previousUrl = currentUrl;
      return;
    }

    if (this.shouldLogNavigation(currentUrl)) {
      this.pushToHistory(this.previousUrl);
    } else {
      this.navigationHistory = [];
    }

    this.previousUrl = currentUrl;
  }

  // Appends url to history (if not already the last entry), skipping any marked-as-deleted URL.
  private pushToHistory(url: string): void {
    const cleanUrl = url.split('?')[0];
    if (this.urlToSkip === cleanUrl) {
      this.urlToSkip = null;
      return;
    }
    const lastEntry = this.navigationHistory[this.navigationHistory.length - 1];
    if (!lastEntry || lastEntry.split('?')[0] !== cleanUrl) {
      this.navigationHistory = [...this.navigationHistory, url];
    }
  }

  // Returns true if navigating to this URL should be recorded in history.
  public shouldLogNavigation(url: string): boolean {
    const cleanUrl = url.split('?')[0];
    return this.routes.some((r) => {
      if (r.logBareRoute) return cleanUrl === r.prefix || cleanUrl.startsWith(r.prefix + '/');
      if (!cleanUrl.startsWith(r.prefix + '/')) return false;
      if (r.logAllSubPaths) return true;
      return !cleanUrl.slice(r.prefix.length + 1).includes('/');
    });
  }

  // Returns a snapshot of the current navigation history stack.
  public getNavigationHistory(): string[] {
    return [...this.navigationHistory];
  }

  // Returns the parent route for a given URL, used as a fallback when history is empty.
  public getParentUrl(url: string): string {
    const cleanUrl = url.split('?')[0];
    const route = this.routes.find((r) => cleanUrl === r.prefix || cleanUrl.startsWith(r.prefix + '/'));
    return route?.parent ?? route?.prefix ?? '/home';
  }

  // Flags the next navigation event as a back-navigation so the logger pops instead of pushing.
  public setBackNavigationFlag(): void {
    this.isBackNavigation = true;
  }

  // Removes a URL from history and prevents it from being re-pushed on the next navigation (e.g. after deleting an entity).
  public markAsRemoved(url: string): void {
    const cleanUrl = url.split('?')[0];
    this.navigationHistory = this.navigationHistory.filter((u) => u.split('?')[0] !== cleanUrl);
    this.urlToSkip = cleanUrl;
  }
}
